import { classNames, isDelayed, isFinishedOrInterrupted } from '@sportal365/smp-widget-utils';
import {
  ConfigContext,
  GameScore,
  GameTeam,
  OddsDisplay,
  SMPContainer,
  SMPFlex,
  SMPLinkWrapper,
  SMPSportEntity,
  SMPText,
  StyledSMPTextVariants,
  useSizeContext,
} from '@velinsi/smp-widgets-ui';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { GenericSize, INLINE_ODDS_BREAKPOINT, MAX_MOBILE_WIDTH } from '../../../../../../packages/constants/generic';
import { FINISHED, INTERRUPTED, LIVE, NOT_STARTED } from '../../../../../../packages/constants/statusTypes';
import useResizeObserver from '../../../../../../packages/hooks/useResizeObserver';
import useOverlapOdds from '../../../../../../packages/hooks/useOverlapOdds';
import { styled } from '../../../../../../packages/theme/stitches.config';
import { INormalizedFootballGameData, IOddsV2, ISubscriberInfo, OddsMarketScope } from '../../../../../../packages/types';
import { INormalizedLivescoreMatchData, ITeamWinnerForm } from '../../../types';
import MatchContainer from '../../MatchContainer';
import {
  BASKETBALL_LIVESCORE,
  BASKETBALL_TOURNAMENT_PROGRAMME,
  LIVESCORE,
  PROGRAMME,
  TENNIS_LIVESCORE,
  TENNIS_TOURNAMENT_PROGRAMME,
  TOURNAMENT_PROGRAMME,
} from '../../../../../../packages/constants/widgetIds';
import { getMatchColorAndStatus } from '../../../helpers/matchStatuses';

const display = (currentSize: GenericSize | undefined, targetSize: GenericSize): React.CSSProperties['display'] => {
  if (currentSize === undefined) {
    if (
      (targetSize === GenericSize.DEFAULT && window.innerWidth >= INLINE_ODDS_BREAKPOINT) ||
      (targetSize === GenericSize.SMALL && window.innerWidth < INLINE_ODDS_BREAKPOINT)
    ) {
      return undefined;
    }

    return 'none';
  }

  return undefined;
};

export interface IMatchListProps {
  matches?:
    | INormalizedLivescoreMatchData[]
    | {
        info?: INormalizedFootballGameData;
        teamWinnerForm?: ITeamWinnerForm;
        odds?: IOddsV2;
      }[];
  handleClick: (info: ISubscriberInfo, clickType: string) => void;
  showOdds?: boolean;
  handleEventClick?: (id: string | number) => void;
  allowInlineOdds?: boolean;
  showTeamWinnerForm?: boolean;
  selectedTeamId?: string;
  oneLine?: boolean; // Used only in MatchesH2HWidget
  oddsMarketScope?: OddsMarketScope;
}

const MatchRow: FC<
  {
    match:
      | INormalizedLivescoreMatchData
      | {
          info?: INormalizedFootballGameData;
          teamWinnerForm?: ITeamWinnerForm;
          odds?: IOddsV2;
        };
    size: GenericSize | undefined;
    areBettingOddsOverlapVisible?: boolean;
  } & Omit<IMatchListProps, 'matches'>
> = ({
  match,
  showOdds,
  handleClick,
  handleEventClick,
  oneLine: isHeadToHead,
  size,
  showTeamWinnerForm,
  selectedTeamId,
  allowInlineOdds,
  oddsMarketScope,
  areBettingOddsOverlapVisible,
}) => {
  const { dataWidgetId } = useContext(ConfigContext);
  const isLivescore = dataWidgetId === LIVESCORE || dataWidgetId === BASKETBALL_LIVESCORE || dataWidgetId === TENNIS_LIVESCORE;
  const isTournamentProgramme =
    dataWidgetId === TOURNAMENT_PROGRAMME ||
    dataWidgetId === PROGRAMME ||
    dataWidgetId === BASKETBALL_TOURNAMENT_PROGRAMME ||
    dataWidgetId === TENNIS_TOURNAMENT_PROGRAMME;

  const [shouldShowDate, date] = match?.info?.date as [boolean, string];

  const statusTextVariant = (() => {
    if (match?.info?.matchStatus?.type === LIVE) return 'live';

    if (match?.info?.matchStatus?.type === FINISHED) return 'livescoreFullTime';

    return 'primaryGray';
  })();

  const statusTextFontSize = (() => {
    if (isFinishedOrInterrupted(match?.info?.matchStatus?.type)) {
      return '2';
    }
    return size !== GenericSize.SMALL ? '3' : '2';
  })();

  const selectedHomeTeamTextVariant =
    selectedTeamId && selectedTeamId === match?.info?.homeTeam?.id
      ? 'matchesH2HSelectedTeamStyle'
      : ('matchesH2HTeamStyle' as StyledSMPTextVariants['variant']);

  const selectedAwayTeamTextVariant =
    selectedTeamId && selectedTeamId === match?.info?.awayTeam?.id
      ? 'matchesH2HSelectedTeamStyle'
      : ('matchesH2HTeamStyle' as StyledSMPTextVariants['variant']);

  const teams = useMemo(() => {
    if (!match.info) {
      return;
    }

    return [
      <GameTeam
        key={match.info.homeTeam?.id}
        aggregateWinnerId={match?.info?.aggregateWinnerId}
        winnerReason={match.info.winnerReason}
        winnerId={match?.info?.winnerId}
        imageVariant={size === GenericSize.SMALL ? 'mobileTeamIcon' : 'largeLivescoreEntityLogo'}
        team={match?.info?.homeTeam}
        teamName={match?.info?.homeTeam?.name}
        teamLogo={match?.info?.homeTeam?.logo}
        sportEntityVariant={size !== GenericSize.SMALL ? 'homeTeam' : undefined}
        textVariant={isHeadToHead ? selectedHomeTeamTextVariant : undefined}
      />,
      <GameTeam
        key={match.info.awayTeam?.id}
        winnerReason={match.info.winnerReason}
        winnerId={match?.info?.winnerId}
        aggregateWinnerId={match?.info?.aggregateWinnerId}
        imageVariant={size === GenericSize.DEFAULT ? 'largeLivescoreEntityLogo' : 'mobileTeamIcon'}
        team={match?.info?.awayTeam}
        teamName={match?.info?.awayTeam?.name}
        teamLogo={match?.info?.awayTeam?.logo}
        textVariant={isHeadToHead ? selectedAwayTeamTextVariant : undefined}
      />,
    ];
  }, [match.info, selectedAwayTeamTextVariant, selectedHomeTeamTextVariant, size, isHeadToHead]);

  const gameScore = useMemo(() => {
    if (!match.info) {
      return null;
    }

    let additionalProps: Record<string, string> = {};

    if (size !== GenericSize.SMALL) {
      additionalProps = {
        fontSize: '16px',
        fontWeight: '400',
        delimiter: '-',
      };
    }

    if (match?.info?.matchStatus?.type === INTERRUPTED) {
      additionalProps = {
        ...additionalProps,
        color: 'var(--colors-teamLostColor)',
      };
    }

    return (
      <SMPFlex css={{ py: size !== GenericSize.DEFAULT ? '$3' : undefined, pr: size !== GenericSize.DEFAULT ? '$3' : undefined }}>
        <GameScore
          eventId={match?.info?.id}
          verticalAlign={size !== GenericSize.DEFAULT}
          homeTeamScore={isDelayed(match.info.matchStatus?.type) ? '-' : match.info?.homeTeamScore}
          awayTeamScore={isDelayed(match.info.matchStatus?.type) ? '-' : match.info?.awayTeamScore}
          {...additionalProps}
        />
      </SMPFlex>
    );
  }, [match.info, size]);

  const getMatchStatus = useCallback(
    (isBetweenTeams: boolean) => {
      if (!match.info) {
        return null;
      }

      const { status, color } = getMatchColorAndStatus(match, isBetweenTeams);

      return (
        <SMPText
          variant={statusTextVariant}
          size={isBetweenTeams ? '3' : statusTextFontSize}
          style={{
            fontWeight: !isBetweenTeams && isFinishedOrInterrupted(match?.info?.matchStatus?.type) ? 600 : 400,
            color,
          }}
        >
          {status}
        </SMPText>
      );
    },
    [match, statusTextVariant, statusTextFontSize],
  );

  const matchStatus = useMemo(() => getMatchStatus, [getMatchStatus]);

  const teamWinnerForm = useMemo(() => {
    if (!showTeamWinnerForm) {
      return null;
    }
    return (
      <SMPFlex align='center'>
        <SMPText variant='teamWinnerForm' css={{ background: match?.teamWinnerForm?.css?.background }}>
          {match?.teamWinnerForm?.value}
        </SMPText>
      </SMPFlex>
    );
  }, [match?.teamWinnerForm?.css?.background, match?.teamWinnerForm?.value, showTeamWinnerForm]);

  const isUpcoming = useMemo(() => {
    if (!match.info?.matchStatus?.type) {
      return false;
    }

    return match.info.matchStatus.type !== LIVE && match.info.matchStatus.type !== FINISHED;
  }, [match.info]);

  const shouldShowStatus = useMemo(() => {
    if (!match.info?.matchStatus?.type) {
      return false;
    }

    return (
      isFinishedOrInterrupted(match.info.matchStatus.type) ||
      match.info.matchStatus.type === LIVE ||
      (size !== GenericSize.DEFAULT && match.info.matchStatus.type === NOT_STARTED)
    );
  }, [match, size]);

  const dateAndStatus = useMemo(() => {
    if (!match.info) {
      return null;
    }

    const getMobileDateText = (date: string) => {
      if (size === GenericSize.SMALL) {
        const dateParts = date.split(' ');

        return (
          <div>
            <SMPText textAlign='center' size='2' variant='primaryGray'>
              {dateParts[0]}
            </SMPText>
            <SMPText textAlign='center' size='2' variant='primaryGray'>
              {dateParts[1]}
            </SMPText>
          </div>
        );
      }

      return (
        <SMPText textAlign='center' size='2' variant='primaryGray'>
          {date}
        </SMPText>
      );
    };

    return (
      <SMPFlex
        className='date-and-status-wrapper'
        direction='column'
        align='center'
        justify='center'
        gap={size === GenericSize.SMALL ? 'md' : 'sm'}
        style={{ minWidth: size === GenericSize.SMALL ? '50px' : '75px' }}
        css={{ mr: size !== GenericSize.DEFAULT ? '$2' : undefined }}
      >
        <SMPFlex align='center'>{getMobileDateText(date)}</SMPFlex>
        {shouldShowStatus && matchStatus(false)}
      </SMPFlex>
    );
  }, [date, shouldShowStatus, match.info, matchStatus, size]);

  if (!match.info) return null;

  const renderOdds = showOdds && !!match.odds && match.odds.length > 0;
  const eventLinkProps = {
    href: match.info.matchLink,
    target: match.info.urlTarget,
    onClick: () => handleEventClick && match.info?.id && handleEventClick(match.info.id),
  };

  return (
    <Wrapper
      className={classNames({
        'default-size': size !== GenericSize.SMALL,
        'small-size': size !== GenericSize.DEFAULT,
      })}
    >
      <MatchContainer variant={match?.info?.matchStatus?.type === 'LIVE' ? 'live' : 'base'} type='livescore'>
        <SMPFlex form={showTeamWinnerForm} grid={size !== GenericSize.DEFAULT} css={{ py: 0, pr: 0 }}>
          {size !== GenericSize.DEFAULT && (
            <>
              <SMPLinkWrapper {...eventLinkProps}>
                <SMPFlex justify='start' css={{ py: '14px' }} style={{ display: display(size, GenericSize.SMALL), overflow: 'hidden' }}>
                  {dateAndStatus}
                  <SMPFlex variant='column' align='start'>
                    {teams}
                  </SMPFlex>
                </SMPFlex>
                <SMPFlex>
                  {gameScore}
                  {teamWinnerForm}
                </SMPFlex>
              </SMPLinkWrapper>
            </>
          )}
          {size !== GenericSize.SMALL && (
            <DefaultSizeContainer className={renderOdds ? 'with-odds' : ''} style={{ display: display(size, GenericSize.DEFAULT) }}>
              <SMPLinkWrapper {...eventLinkProps}>
                {dateAndStatus}
                <DefaultSizeTeamsContainer withBorder={allowInlineOdds}>
                  {teams?.[0]}
                  {isUpcoming && (isLivescore || isTournamentProgramme) && (
                    <SMPFlex variant='column' align='center' justify='center'>
                      {matchStatus(true)}
                    </SMPFlex>
                  )}
                  {!isUpcoming && gameScore}
                  {teams?.[1]}
                </DefaultSizeTeamsContainer>
              </SMPLinkWrapper>
              {renderOdds ? (
                <OddsDisplay
                  odds={match.odds!}
                  handleClick={handleClick}
                  marketSelectionDisplay={false}
                  oddsMarketScope={oddsMarketScope}
                  areBettingOddsOverlapVisible={areBettingOddsOverlapVisible}
                />
              ) : (
                <OddsPlaceholder />
              )}
            </DefaultSizeContainer>
          )}
        </SMPFlex>
      </MatchContainer>
      {size !== GenericSize.DEFAULT && renderOdds && (
        <SMPContainer variant='oddsContainerSingleLine' style={{ display: display(size, GenericSize.SMALL) }}>
          <OddsDisplay
            areBettingOddsOverlapVisible={areBettingOddsOverlapVisible}
            odds={match.odds!}
            handleClick={handleClick}
            marketSelectionDisplay={false}
            oddsMarketScope={oddsMarketScope}
          />
        </SMPContainer>
      )}
    </Wrapper>
  );
};

export const DefaultSizeContainer = styled.withConfig({ componentId: 'MatchRow', displayName: 'DefaultSizeContainer' })('div', {
  display: 'grid',
  width: '100%',
  gridTemplateColumns: '75px minmax(0, 1fr) 400px',

  [`& > *, & > ${SMPLinkWrapper} > *`]: {
    py: '14px',
    px: '$3',
  },

  '&.with-odds': {
    [`& > ${SMPContainer}`]: {
      display: 'flex',
      mx: 'unset',
      pl: '$3',

      [`& > ${SMPContainer}`]: {
        width: '100%',
        px: 'unset',
      },
    },
  },
});

export const Wrapper = styled.withConfig({ componentId: 'MatchRow', displayName: 'Wrapper' })('div', {
  '&.default-size': {
    marginBottom: 0,

    [`${SMPSportEntity}`]: {
      py: 0,
    },
  },
  '&.small-size': {
    marginBottom: '1px',
  },
});

export const DefaultSizeTeamsContainer = styled.withConfig({ componentId: 'MatchRow', displayName: 'DefaultSizeTeamsContainer' })('div', {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) max-content minmax(0, 1fr)',
  gap: '$3',
  alignItems: 'center',

  [`${SMPText}`]: {
    whiteSpace: 'unset',
  },
  variants: {
    withBorder: {
      true: {
        borderRight: `1px solid $primaryBackgroundColor`,
      },
    },
  },
});

const OddsPlaceholder = styled.withConfig({ componentId: 'MatchRow', displayName: 'OddsPlaceholder' })('div', {
  backgroundColor: '$oddsContainerPrimaryBgColor',
});

function MatchList({ matches, allowInlineOdds = true, oneLine, ...rest }: IMatchListProps): JSX.Element {
  const { dataBettingLogoOverlap } = useContext(ConfigContext);
  const { setIsMobile } = useSizeContext();
  const [size, setSize] = useState<GenericSize>();
  const { areBettingOddsOverlapVisible } = useOverlapOdds({ dataBettingLogoOverlap });
  const measuredRef = useResizeObserver((fieldElement) => {
    setIsMobile(fieldElement.offsetWidth < MAX_MOBILE_WIDTH);
    setSize((allowInlineOdds || oneLine) && fieldElement.offsetWidth >= INLINE_ODDS_BREAKPOINT ? GenericSize.DEFAULT : GenericSize.SMALL);
  });

  return (
    <SMPContainer ref={measuredRef} variant='paddinglessContainer' style={{ opacity: Number(!!size) }}>
      {matches?.map((match) => {
        return (
          <MatchRow
            key={match.info?.id}
            areBettingOddsOverlapVisible={areBettingOddsOverlapVisible}
            match={match}
            oneLine={oneLine}
            size={size}
            {...rest}
            allowInlineOdds={allowInlineOdds}
          />
        );
      })}
    </SMPContainer>
  );
}

export default MatchList;
